/**
 * The shell store — windows, z-order, toasts. One plain external store consumed
 * via useSyncExternalStore so drag/resize can commit without re-rendering the
 * whole desktop, and non-React code (shortcuts) can drive it.
 *
 * Z-ORDER IS THE STACK ARRAY. `zIndex = stack.indexOf(id)`, focused window =
 * `stack.at(-1)`. Focusing moves an id to the end. No counters, no arithmetic.
 */

export interface WindowBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WindowState {
  id: string;
  appId: string;
  title: string;
  bounds: WindowBounds;
  /** restore target after maximize */
  prevBounds: WindowBounds | null;
  minimized: boolean;
  maximized: boolean;
  launchParams?: Record<string, string> | undefined;
}

export interface Toast {
  id: string;
  kind: "info" | "ok" | "err";
  title: string;
  body?: string;
}

export interface ShellState {
  windows: Record<string, WindowState>;
  /** z-order; last entry is focused */
  stack: string[];
  toasts: Toast[];
}

/* ---------- store plumbing ---------- */

let state: ShellState = { windows: {}, stack: [], toasts: [] };
const listeners = new Set<() => void>();

function emit(next: ShellState) {
  state = next;
  for (const fn of listeners) fn();
  schedulePersist();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getState(): ShellState {
  return state;
}

/* ---------- persistence ----------
   Layout survives reload. Toasts don't. Restored bounds are clamped to the
   current viewport — screens change between sessions. */

const PERSIST_KEY = "radar.shell.v1";
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persistNow() {
  try {
    const { windows, stack } = state;
    localStorage.setItem(PERSIST_KEY, JSON.stringify({ windows, stack }));
  } catch {
    /* private mode / quota — layout persistence is a nicety */
  }
}

function schedulePersist() {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(persistNow, 500);
}

// Flush the debounce on the way out, or the last ~500ms of changes (a close
// right before a reload) resurrect on the next boot.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (persistTimer) clearTimeout(persistTimer);
    persistNow();
  });
}

export function restoreLayout(): void {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw) as Pick<ShellState, "windows" | "stack">;
    if (!saved.windows || !saved.stack) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const windows: Record<string, WindowState> = {};
    for (const id of saved.stack) {
      const win = saved.windows[id];
      if (!win) continue;
      windows[id] = { ...win, bounds: clampBounds(win.bounds, vw, vh) };
    }
    emit({ windows, stack: saved.stack.filter((id) => windows[id]), toasts: [] });
  } catch {
    /* corrupted layout → fresh desktop */
  }
}

export function clampBounds(b: WindowBounds, vw: number, vh: number): WindowBounds {
  const w = Math.min(b.w, vw - 24);
  const h = Math.min(b.h, vh - 60);
  return {
    w,
    h,
    // keep at least 120px of title bar reachable, and never under the menu bar
    x: Math.min(Math.max(b.x, -w + 120), vw - 120),
    y: Math.min(Math.max(b.y, 30), vh - 60),
  };
}

/* ---------- window actions ---------- */

let idCounter = 0;
function nextId(appId: string): string {
  idCounter += 1;
  return `${appId}-${idCounter.toString(36)}-${Date.now().toString(36)}`;
}

/** Cascade new windows from a base offset so they don't stack exactly. */
function spawnBounds(defaults: { w: number; h: number }, openCount: number): WindowBounds {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(defaults.w, vw - 48);
  const h = Math.min(defaults.h, vh - 96);
  const cascade = (openCount % 8) * 28;
  return clampBounds(
    { x: Math.round((vw - w) / 2) + cascade, y: Math.round((vh - h) / 2.4) + cascade, w, h },
    vw,
    vh,
  );
}

export interface OpenOptions {
  appId: string;
  title: string;
  defaultBounds: { w: number; h: number };
  singleton: boolean;
  launchParams?: Record<string, string> | undefined;
}

/** Open (or focus, for singletons) a window. Returns the window id. */
export function openWindow(opts: OpenOptions): string {
  if (opts.singleton) {
    const existing = Object.values(state.windows).find((w) => w.appId === opts.appId);
    if (existing) {
      const updated: WindowState = {
        ...existing,
        minimized: false,
        launchParams: opts.launchParams ?? existing.launchParams,
      };
      emit({
        ...state,
        windows: { ...state.windows, [existing.id]: updated },
        stack: [...state.stack.filter((id) => id !== existing.id), existing.id],
      });
      return existing.id;
    }
  }
  const id = nextId(opts.appId);
  const win: WindowState = {
    id,
    appId: opts.appId,
    title: opts.title,
    bounds: spawnBounds(opts.defaultBounds, state.stack.length),
    prevBounds: null,
    minimized: false,
    maximized: false,
    launchParams: opts.launchParams,
  };
  emit({ ...state, windows: { ...state.windows, [id]: win }, stack: [...state.stack, id] });
  return id;
}

export function closeWindow(id: string): void {
  if (!state.windows[id]) return;
  const windows = { ...state.windows };
  delete windows[id];
  emit({ ...state, windows, stack: state.stack.filter((w) => w !== id) });
}

export function focusWindow(id: string): void {
  if (!state.windows[id] || state.stack.at(-1) === id) return;
  emit({ ...state, stack: [...state.stack.filter((w) => w !== id), id] });
}

export function minimizeWindow(id: string): void {
  const win = state.windows[id];
  if (!win) return;
  // focus falls to the next unminimized window
  const rest = state.stack.filter((w) => w !== id && !state.windows[w]?.minimized);
  emit({
    ...state,
    windows: { ...state.windows, [id]: { ...win, minimized: true } },
    stack: [id, ...state.stack.filter((w) => w !== id && !rest.includes(w)), ...rest],
  });
}

export function restoreWindow(id: string): void {
  const win = state.windows[id];
  if (!win) return;
  emit({
    ...state,
    windows: { ...state.windows, [id]: { ...win, minimized: false } },
    stack: [...state.stack.filter((w) => w !== id), id],
  });
}

export function toggleMaximize(id: string): void {
  const win = state.windows[id];
  if (!win) return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const next: WindowState = win.maximized
    ? { ...win, maximized: false, bounds: win.prevBounds ?? win.bounds, prevBounds: null }
    : {
        ...win,
        maximized: true,
        prevBounds: win.bounds,
        bounds: { x: 8, y: 38, w: vw - 16, h: vh - 100 },
      };
  emit({
    ...state,
    windows: { ...state.windows, [id]: next },
    stack: [...state.stack.filter((w) => w !== id), id],
  });
}

/** Commit final bounds after a drag/resize gesture (the gesture itself writes
    straight to the DOM node; React state only changes once, here). */
export function commitBounds(id: string, bounds: WindowBounds): void {
  const win = state.windows[id];
  if (!win) return;
  emit({ ...state, windows: { ...state.windows, [id]: { ...win, bounds, maximized: false } } });
}

export function setLaunchParams(id: string, launchParams: Record<string, string>): void {
  const win = state.windows[id];
  if (!win) return;
  emit({ ...state, windows: { ...state.windows, [id]: { ...win, launchParams } } });
}

/** Cycle focus to the next unminimized window (⌘`). */
export function cycleFocus(): void {
  const visible = state.stack.filter((id) => !state.windows[id]?.minimized);
  if (visible.length < 2) return;
  const next = visible[0]!;
  focusWindow(next);
}

/* ---------- toasts ---------- */

export function pushToast(toast: Omit<Toast, "id"> & { timeoutMs?: number }): void {
  const id = nextId("toast");
  const { timeoutMs = 5000, ...rest } = toast;
  emit({ ...state, toasts: [...state.toasts, { ...rest, id }] });
  if (timeoutMs > 0) {
    setTimeout(() => dismissToast(id), timeoutMs);
  }
}

export function dismissToast(id: string): void {
  if (!state.toasts.some((t) => t.id === id)) return;
  emit({ ...state, toasts: state.toasts.filter((t) => t.id !== id) });
}
